import { useAsync } from 'react-use';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { Entity } from '@backstage/catalog-model';
import { stringifyEntityRef } from '@backstage/catalog-model';

export const LINEARB_TEAM_ANNOTATION = 'linearb.io/team-id';

export function hasLinearBTeam(entity: Entity): boolean {
  return Boolean(entity.metadata.annotations?.[LINEARB_TEAM_ANNOTATION]);
}

export type DoraBand = 'elite' | 'high' | 'medium' | 'low';

export interface DoraTrendPoint {
  date: string;
  value: number;
}

export interface DoraMetric {
  key: string;
  label: string;
  value: number | null;
  displayValue: string;
  band: DoraBand | null;
  trend: DoraTrendPoint[];
  previousValue: number | null;
}

export interface DoraSummary {
  metrics: DoraMetric[];
  periodStart: string;
  periodEnd: string;
}

export function useLinearBDora(
  entity: Entity,
  windowDays = 90,
): {
  loading: boolean;
  error: Error | undefined;
  data: DoraSummary | undefined;
} {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const entityRef = stringifyEntityRef(entity);

  const {
    loading,
    error,
    value: data,
  } = useAsync(async () => {
    if (!hasLinearBTeam(entity)) return undefined;

    const baseUrl = await discoveryApi.getBaseUrl('linearb');
    const params = new URLSearchParams({
      entityRef,
      window: String(windowDays),
    });
    const response = await fetchApi.fetch(`${baseUrl}/dora-summary?${params}`);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        body.error?.message ??
          body.error ??
          `LinearB API error: ${response.status}`,
      );
    }
    return (await response.json()) as DoraSummary;
  }, [entityRef, windowDays]);

  return { loading, error, data };
}
